import React, { type Dispatch, type SetStateAction } from 'react';

import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router';

import { Text, theme } from '@prose-ui';
import { styled } from '@prose-ui/legacy';
import { mediaMinWidth } from '@prose-ui/utils/media';

import { MobileInfoIconTooltip } from 'Apps/Main/Scenes/HomeV2/InfoIconTooltip';

import { Image, type StaticImageData } from 'Components/Image';
import { Tooltip } from 'Components/PortalTooltip';
import { ProductPriceModule } from 'Components/ProductPriceModule';
import Spacer from 'Components/Spacer';

import { productsSlugs } from 'constants/products';

import { stepSerum, stepSupplements } from 'assets/content/densitySetLandingPage';
import infoImgDark from 'assets/images/homeRevamp/dark-info-icon.svg';

import useResponsiveVariant from 'utils/useResponsiveVariant';

const SvgWrapper = styled.svg`
  display: block;
  margin: 10px auto;
  max-height: 85px;
`;

const CloseModalButton = styled.button`
  width: 100%;

  padding: 0;
  appearance: none;
  background: none;
  border: none;

  cursor: pointer;
`;

const GraphText = styled.text`
  text-align: center;
  fill: ${theme.colors.primary[400]};
  font-size: 10px;
  font-family: ${theme.typography.fontFamily.body};
  font-weight: ${theme.typography.fontWeight.medium};
  letter-spacing: ${theme.typography.letterSpacing.body};
`;

const PathAround = styled.path`
  stroke: ${theme.colors.primary[100]};
  fill: none;
  stroke-width: 2.5;
`;

const PathPercent = styled.path`
  stroke: ${theme.colors.primary[200]};
  fill: none;
  stroke-width: 2.5;
  animation: progress 1s ease-out forwards;

  @keyframes progress {
    0% {
      stroke-dasharray: 0 100;
    }
  }
`;

type CircleGraphProps = {
  percentage: number;
};

const CircleGraph = ({ percentage }: CircleGraphProps) => {
  const strokeDasharray = `${(percentage / 100) * 100}, 100`;

  return (
    <SvgWrapper viewBox="0 0 36 36">
      <PathAround d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
      <PathPercent
        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
        strokeDasharray={strokeDasharray}
      />
      <GraphText dy="7" textAnchor="middle" x="18" y="14">
        {`${percentage}%`}
      </GraphText>
    </SvgWrapper>
  );
};

export const Separator = styled.div<{ isMobile: boolean }>`
  height: 1px;
  width: 100%;
  background-color: ${theme.colors.primary[100]};
  margin: ${theme.spacing['4x']} 0;
  ${mediaMinWidth('sm')} {
    margin: ${theme.spacing['8x']} 0;
  }
`;

type StepHeaderProps = {
  link: string;
  step: string;
  subtitle: string;
  title: React.ReactNode;
  dataFrom?: string;
  dataClick?: string;
};

const StepHeader = ({ link, step, subtitle, title, dataFrom, dataClick }: StepHeaderProps) => {
  const { isMobile } = useResponsiveVariant({ legacy: false });
  return (
    <div>
      <Text variant={isMobile ? 'labelSm' : 'labelMd'}>{step}</Text>
      <Link to={link}>
        <Text
          as="h3"
          data-click={dataClick}
          data-from={dataFrom}
          variant={isMobile ? 'body3medium' : 'body1medium'}
        >
          {title}
        </Text>
      </Link>
      <Text color="neutral.800" variant={isMobile ? 'body5regular' : 'body4regular'}>
        {subtitle}
      </Text>
    </div>
  );
};

const StepBodyWrapper = styled.div`
  border: 1px solid transparent;
`;

const StepBodyKeyResultWrapper = styled.div`
  width: 100%;
  display: flex;
  gap: ${theme.spacing['3x']};
  justify-content: center;

  height: 185px;
  ${mediaMinWidth('md')} {
    justify-content: center;
  }
`;

const KeyResult = styled.div`
  height: 100%;
  max-width: 150px;
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: ${theme.spacing['2x']};
`;

const TooltipWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing['1x']};
  height: ${theme.spacing['3x']};
`;

type Slug = (typeof productsSlugs)[keyof typeof productsSlugs];

type StepBodyProps = {
  dataFrom: string;
  productSlug: Slug;
  body: string;
  numbers: Array<{
    key: string;
    result: number;
  }>;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  tooltip: string;
};

const TooltipContent = ({ content, onClose }: { content: string; onClose?: () => void }) => (
  <>
    <Text variant="body5regular">{content}</Text>
    {onClose && (
      <CloseModalButton onClick={onClose}>
        <Text marginLeft="auto" variant="label3regular" width="fit">
          close x
        </Text>
      </CloseModalButton>
    )}
  </>
);

const StepBody = ({ body, dataFrom, numbers, setIsOpen, productSlug, tooltip }: StepBodyProps) => {
  const { isMobile } = useResponsiveVariant({ legacy: false });
  const [numbersRef, numbersInView] = useInView({
    threshold: 0.3,
    triggerOnce: true,
  });

  return (
    <StepBodyWrapper>
      <Text variant="body3regular">{body}</Text>
      <Spacer size={isMobile ? 16 : 24} />

      <TooltipWrapper>
        <Text variant="body4medium">Scientifically tested</Text>

        {isMobile ? (
          <MobileInfoIconTooltip
            dataClick="tooltip"
            dataFrom={dataFrom}
            iconColor="dark"
            setIsOpen={setIsOpen}
          />
        ) : (
          <Tooltip
            arrowSize={10}
            content={<TooltipContent content={tooltip} />}
            dataClick="tooltip"
            dataFrom={dataFrom}
            hasBeforePointer={false}
            maxWidth={238}
            openerComponent={<Image alt="" height={16} src={infoImgDark} width={16} />}
            openerHeight={16}
            openerWidth={16}
            style={{
              padding: theme.spacing['5x'],
              fontSize: '12px',
              lineHeight: 'initial',
              backgroundColor: theme.colors.neutral[200],
            }}
            topPlus={0}
          />
        )}
      </TooltipWrapper>

      <Spacer size={isMobile ? 16 : 24} />

      <StepBodyKeyResultWrapper ref={numbersRef}>
        {numbers.map((number) => {
          return (
            <KeyResult key={number.key}>
              {numbersInView && <CircleGraph percentage={number.result} />}

              <Text as="h4" textAlign="center" variant="body3regular">
                {number.key}
              </Text>
            </KeyResult>
          );
        })}
      </StepBodyKeyResultWrapper>
      <Spacer size={isMobile ? 16 : 24} />
      <ProductPriceModule productSlug={productSlug} />
    </StepBodyWrapper>
  );
};

const StepRootWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  overflow: hidden;

  ${mediaMinWidth('md')} {
    border: 1px solid ${theme.colors.primary[100]};
    border-radius: ${theme.radius.xs};
  }
`;

const StepWrapper = styled.div<{ direction?: string }>`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing['4x']};
  ${mediaMinWidth('md')} {
    flex-direction: ${({ direction }) => direction ?? 'row'};
  }
`;

const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  ${mediaMinWidth('md')} {
    width: 50%;
    padding: ${theme.spacing['12x']} ${theme.spacing['14x']};
  }
`;

const StyledImage = styled(Image)`
  height: auto;
  width: 100%;
  object-fit: cover;
  ${mediaMinWidth('md')} {
    width: 50%;
  }
`;

type StepContentProps = {
  body: string;
  dataFrom: string;
  dataClick: string;
  direction?: 'row-reverse';
  image: {
    mobile: StaticImageData;
    desktop: StaticImageData;
    alt: string;
  };
  link: string;
  numbers: Array<{
    key: string;
    result: number;
  }>;
  productSlug: Slug;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  step: string;
  subtitle: string;
  title: React.ReactNode;
  tooltip: string;
};

const Step = ({
  body,
  dataFrom,
  direction,
  image,
  link,
  numbers,
  productSlug,
  setIsOpen,
  step,
  subtitle,
  title,
  tooltip,
  dataClick,
}: StepContentProps) => {
  const { isMobile, isTablet } = useResponsiveVariant({ legacy: false });

  return (
    <StepRootWrapper>
      <StepWrapper direction={direction}>
        {(isMobile || isTablet) && (
          <StepHeader
            dataClick={dataClick}
            dataFrom={dataFrom}
            link={link}
            step={step}
            subtitle={subtitle}
            title={title}
          />
        )}

        <StyledImage
          alt={image.alt}
          src={isMobile ? image.mobile : isTablet ? image.mobile : image.desktop}
        />

        <StepContainer>
          {!isMobile && !isTablet && (
            <StepHeader
              dataClick={dataClick}
              dataFrom={dataFrom}
              link={link}
              step={step}
              subtitle={subtitle}
              title={title}
            />
          )}

          {!isMobile && !isTablet && <Separator isMobile={isMobile} />}

          <StepBody
            body={body}
            dataFrom={dataFrom}
            numbers={numbers}
            productSlug={productSlug}
            setIsOpen={setIsOpen}
            tooltip={tooltip}
          />
        </StepContainer>
      </StepWrapper>
    </StepRootWrapper>
  );
};

type StepProps = {
  setIsOpen: Dispatch<SetStateAction<boolean>>;
};

export const SupplementsStep = ({ setIsOpen }: StepProps) => (
  <Step
    body={stepSupplements.body}
    dataClick="supplements-pdp"
    dataFrom="supplements-module"
    direction="row-reverse"
    image={stepSupplements.image}
    link={stepSupplements.link}
    numbers={stepSupplements.numbers}
    productSlug={stepSupplements.slug}
    setIsOpen={setIsOpen}
    step={stepSupplements.step}
    subtitle={stepSupplements.subtitle}
    title={stepSupplements.title}
    tooltip={stepSupplements.tooltip}
  />
);

export const SerumStep = ({ setIsOpen }: StepProps) => (
  <Step
    body={stepSerum.body}
    dataClick="scalp-serum-pdp"
    dataFrom="scalp-serum-module"
    image={stepSerum.image}
    link={stepSerum.link}
    numbers={stepSerum.numbers}
    productSlug={stepSerum.slug}
    setIsOpen={setIsOpen}
    step={stepSerum.step}
    subtitle={stepSerum.subtitle}
    title={stepSerum.title}
    tooltip={stepSerum.tooltip}
  />
);

export const StepSection = ({ children }: { children: React.ReactNode }) => {
  const { isMobile, isTablet } = useResponsiveVariant({ legacy: false });

  return (
    <>
      <Text
        as="h2"
        textAlign={isMobile || isTablet ? 'left' : 'center'}
        variant={isMobile ? 'h3' : 'h2'}
      >
        Thicker, healthier hair in two simple steps
      </Text>

      <Spacer size={isMobile ? 0 : 32} />

      {isMobile && <Separator isMobile={isMobile} />}
      {children}
    </>
  );
};
